"use client";

import SectionTitle from "@/components/ui/SectionTitle";
import { motion, Variants } from "framer-motion";
import Link from "next/link";
import React, { useEffect, useMemo, useState } from "react";
import PrettyTitle from "@/components/ui/PrettyTitle";
import Image from "@/components/ui/MediaImage";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import type { PageUrlMap } from "@/types/blog";
import { buildCategoryUrl, resolveProductTypeKeyForCategoryUrl } from "@/lib/url-helpers";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";
import { sanitizeCategoryDescriptionHtml } from "@/lib/sanitize-category-description";
import { renderCmsSectionTitle } from "@/lib/render-cms-section-title";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

export interface CardProps {
  svg: string;
  title: string;
  description: string;
  /** Blok önizlemesinde bazen gelmez; Next Link için her zaman normalize edilir */
  link?: string;
  /** Ürün kategorisi kapak görseli (API cover_image / search_tab_icon) */
  imageUrl?: string;
}

/** Next.js Link `href` string ister; editör/block’ta link eksik veya nesne olabilir */
function normalizeCardLink(link: unknown): string {
  if (typeof link === "string" && link.trim()) return link;
  if (link && typeof link === "object" && "url" in link) {
    const u = (link as { url?: string }).url;
    if (typeof u === "string" && u.trim()) return u;
  }
  return "#";
}

const PLACEHOLDER_SVG = `<svg width="64" height="48" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 40h56L48 24H16L4 40z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 24V8h20v16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut" as const,
    },
  },
};

function YachtCard({
  card,
  cardVariants: cv,
  viewMoreLabel,
}: {
  card: CardProps;
  cardVariants: Variants;
  viewMoreLabel: string;
}) {
  const imgSrc = card.imageUrl?.trim() || "";
  const descriptionHtml = sanitizeCategoryDescriptionHtml(card.description);

  return (
    <motion.div
      className="col-span-1 lg:col-span-3 bg-white rounded-lg overflow-hidden group hover:bg-alsero-first transition-colors ease-in-out duration-300 flex flex-col gap-0 drop-shadow-card hover:shadow-2xl"
      variants={cv}
      whileHover={{
        scale: 1.03,
        y: -8,
        transition: {
          duration: 0.3,
          ease: "easeOut",
        },
      }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="relative w-full aspect-[660/491] bg-gradient-to-br from-gray-100 to-gray-200 shrink-0">
        {imgSrc ? (
            <Link
            href={normalizeCardLink(card.link)}
            className="rounded-lg border border-black text-sm tracking-[25%] group/link px-8 py-4 group-hover:bg-white transition-all duration-300 self-start hover:bg-alsero-first hover:text-white"
          >
            <Image
              src={imgSrc}
              alt={card.title || "Yacht category"}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 25vw"
            />
          </Link>

        ) : null}
      </div>
      <div className="px-8 py-11 flex flex-col gap-10">
        <PrettyTitle
          title={card.title}
          tag="h3"
          className="font-bold text-2xl text-alsero-text-dark group-hover:text-white transition-colors duration-300 md:[&>span]:whitespace-normal"
          firstLineWords={1}
          keepLastWords={1}
          clampLines={2}
        />

        {descriptionHtml ? (
          <div
            className="text-lg text-alsero-light-gray group-hover:text-white transition-colors duration-300 line-clamp-4 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5"
            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(descriptionHtml) }}
          />
        ) : (
          <p className="text-lg text-alsero-light-gray group-hover:text-white transition-colors duration-300">
            {card.description}
          </p>
        )}
        <Link
          href={normalizeCardLink(card.link)}
          className="rounded-lg border border-black text-sm tracking-[25%] group/link px-8 py-4 group-hover:bg-white transition-all duration-300 self-start hover:bg-alsero-first hover:text-white"
        >
          {viewMoreLabel}
        </Link>
      </div>

    </motion.div>
  );
}

export interface HomeYachtCatsProps {
  subTitle?: string;
  /** Satır sonu ile vurgulu orta satır: "Satır1\\nVurgu\\nSatır3" */
  title?: string;
  description?: string | null;
  categoryIds?: number[];
  cards?: CardProps[];
  enableClientFetch?: boolean;
  languageId?: number;
  language?: string;
  languageCode?: string;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  categoryUrlProductType?: string;
  __pageContext?: {
    languageId?: number;
    language?: string;
    languageCode?: string;
    pageUrls?: PageUrlMap;
    categoryUrlProductType?: string;
  };
}

const DEFAULT_TITLE = `Check Out Our\nYacht Charter\nOpportunities`;

export default function HomeYachtCats({
  subTitle = "Yacht Charter",
  title = DEFAULT_TITLE,
  description = null,
  categoryIds: propCategoryIds,
  cards: initialCards,
  enableClientFetch: enableClientFetchProp,
  languageId: propLanguageId,
  language: propLanguage,
  languageCode: propLanguageCode,
  pageUrls: propPageUrls,
  defaultLanguage: propDefaultLanguage = "en",
  categoryUrlProductType: propCategoryUrlProductType,
  __pageContext,
}: HomeYachtCatsProps) {
  const enableClientFetch =
    enableClientFetchProp ?? !(Array.isArray(initialCards) && initialCards.length > 0);
  const languageId = propLanguageId ?? __pageContext?.languageId;
  const resolvedLanguage =
    propLanguage ?? propLanguageCode ?? __pageContext?.language ?? __pageContext?.languageCode;
  const pageUrls = propPageUrls ?? __pageContext?.pageUrls;
  const defaultLanguage = propDefaultLanguage;
  const categoryUrlProductType =
    propCategoryUrlProductType ?? __pageContext?.categoryUrlProductType ?? "yacht";

  const { getVariable, language: contextLanguage } = useLanguageVariables();
  const viewMoreFallback =
    (resolvedLanguage || contextLanguage || defaultLanguage).toLowerCase() === "en"
      ? "View more"
      : "Daha fazla";
  const viewMoreLabel = getVariable("home_yacht_cats.view_more", viewMoreFallback);

  const [cards, setCards] = useState<CardProps[]>(() =>
    Array.isArray(initialCards) ? initialCards : []
  );
  const [componentProps, setComponentProps] = useState<{ categoryIds?: number[] }>({});

  useEffect(() => {
    if (!shouldEmbedBlockPropsInDom()) return;
    const el = document.querySelector('[data-component-path="sections/HomeYachtCats"]');
    if (!el) return;
    const attr = el.getAttribute("data-component-props");
    if (!attr) return;
    try {
      const p = JSON.parse(attr) as { categoryIds?: number[] };
      if (p.categoryIds) setComponentProps({ categoryIds: p.categoryIds });
    } catch {
      /* ignore */
    }
  }, []);

  const effectiveCategoryIds = useMemo(
    () => propCategoryIds ?? componentProps.categoryIds,
    [propCategoryIds, componentProps.categoryIds]
  );

  const hasServerCards = Array.isArray(initialCards) && initialCards.length > 0;

  const [loading, setLoading] = useState(() => {
    if (Array.isArray(initialCards) && initialCards.length > 0) return false;
    if (enableClientFetch && (propCategoryIds?.length ?? 0) > 0) return true;
    return false;
  });

  useEffect(() => {
    if (hasServerCards) {
      setCards(initialCards!);
      setLoading(false);
      return;
    }
    if (!enableClientFetch) {
      setLoading(false);
      setCards([]);
      return;
    }
    if (!effectiveCategoryIds?.length) {
      setLoading(false);
      setCards([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (languageId) params.set("language_id", String(languageId));
        effectiveCategoryIds.forEach((id) => params.append("category_ids[]", String(id)));
        const res = await fetch(`/api/product-categories?${params.toString()}`);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        if (cancelled) return;

        const byId = new Map<
          number,
          {
            name?: string;
            description?: string;
            slug?: string;
            type?: string;
            cover_image?: string;
            search_tab_icon?: string;
          }
        >();
        for (const raw of list as {
          id?: number;
          name?: string;
          description?: string;
          slug?: string;
          type?: string;
          cover_image?: string;
          search_tab_icon?: string;
        }[]) {
          if (typeof raw.id === "number" && !Number.isNaN(raw.id)) {
            byId.set(raw.id, raw);
          }
        }

        const built: CardProps[] = effectiveCategoryIds.map((id, index) => {
          const c = byId.get(id);
          const pt = resolveProductTypeKeyForCategoryUrl(c?.type, categoryUrlProductType);
          let link = "#";
          if (c?.slug && pageUrls && resolvedLanguage) {
            const builtUrl = buildCategoryUrl(
              pt,
              String(c.slug),
              pageUrls,
              resolvedLanguage,
              defaultLanguage
            );
            link = typeof builtUrl === "string" && builtUrl.trim() ? builtUrl : "#";
          }
          const svgFromProps =
            Array.isArray(initialCards) &&
            initialCards[index] &&
            typeof initialCards[index].svg === "string"
              ? initialCards[index].svg
              : undefined;
          const rawImg =
            (c?.cover_image && String(c.cover_image).trim()) ||
            (c?.search_tab_icon && String(c.search_tab_icon).trim()) ||
            "";
          const imageUrl = rawImg ? transformMediaUrl(rawImg, null) || rawImg : undefined;
          const imageFromProps =
            Array.isArray(initialCards) && initialCards[index]?.imageUrl?.trim()
              ? initialCards[index].imageUrl!.trim()
              : undefined;
          return {
            svg: svgFromProps || PLACEHOLDER_SVG,
            title: c ? String(c.name ?? "") : "—",
            description: (c?.description || "").trim() || "—",
            link,
            ...(imageFromProps || imageUrl ? { imageUrl: imageFromProps || imageUrl } : {}),
          };
        });
        setCards(built);
      } catch (e) {
        console.error("HomeYachtCats fetch:", e);
        setCards([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    hasServerCards,
    initialCards,
    enableClientFetch,
    effectiveCategoryIds,
    languageId,
    pageUrls,
    resolvedLanguage,
    defaultLanguage,
    categoryUrlProductType,
  ]);

  const titleNode = useMemo(() => renderCmsSectionTitle(title ?? ""), [title]);

  // Örnek kartlar editör önizlemesine aittir; üretimde yalnızca CMS/API verisi gösterilir.
  const displayCards = cards.length > 0
    ? cards
    : (shouldEmbedBlockPropsInDom() ? DEMO_FALLBACK_CARDS : []);

  return (
    <section
      className="bg-alsero-soft-white bg-no-repeat bg-alsero-map-decor py-[130px] relative before:content-[''] before:absolute before:inset-x-0 before:-top-[20px] before:h-[56px] before:bg-top before:bg-repeat-x before:bg-[length:auto_56px] before-alsero-gray-line"
    >
      <div className="w-full max-w-full px-4 sm:px-6 lg:w-7/12 lg:px-0 mx-auto min-w-0">
        <SectionTitle
          title={titleNode}
          titleTag="h2"
          subTitle={subTitle}
          subTitleTag="span"
          description={description}
          descriptionTag={description ? "p" : null}
        />
      </div>
      <div className="w-full max-w-full min-w-0 lg:w-8/12 px-4 sm:px-5 lg:px-0 mx-auto">
        {loading && (
          <div className="mt-14 text-center text-alsero-light-gray text-sm py-12">
            Yükleniyor…
          </div>
        )}
        {!loading &&
          cards.length === 0 &&
          effectiveCategoryIds &&
          effectiveCategoryIds.length > 0 && (
          <div className="mt-14 text-center text-alsero-light-gray border border-dashed rounded-xl py-12">
            Seçilen kategoriler bulunamadı.
          </div>
        )}
        {!loading &&
          !(cards.length === 0 && effectiveCategoryIds && effectiveCategoryIds.length > 0) && (
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-12 mt-14 gap-4"
            variants={containerVariants}
            initial={false}
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {displayCards.map((card, index) => (
              <YachtCard
                key={`yacht-cat-${normalizeCardLink(card.link)}-${index}`}
                card={card}
                cardVariants={cardVariants}
                viewMoreLabel={viewMoreLabel}
              />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}

/** Editör önizlemesi / boş veri: tek satır başlık + 3 örnek kart */
const DEMO_FALLBACK_CARDS: CardProps[] = [
  {
    svg: PLACEHOLDER_SVG,
    title: "Yacht category",
    description: "Select categories in the editor to load real data.",
    link: "#",
  },
  { svg: PLACEHOLDER_SVG, title: "—", description: "—", link: "#" },
  { svg: PLACEHOLDER_SVG, title: "—", description: "—", link: "#" },
];
